import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TOKEN_KEY = 'wanna_token';
export const REFRESH_KEY = 'wanna_refresh_token';

// Refresh proactively when the access token is about to die, so a user who is
// actively using the app never sees a 401 in the first place.
const REFRESH_MARGIN_MS = 5 * 60 * 1000;
const AUTH_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/me'];

let refreshPromise = null;

const safeStorage = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      if (value) localStorage.setItem(key, value);
      else localStorage.removeItem(key);
    } catch {
      /* private mode / storage disabled */
    }
  },
};

export const getToken = () => safeStorage.get(TOKEN_KEY);
export const getRefreshToken = () => safeStorage.get(REFRESH_KEY);

export function setAuthHeader(token) {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
}

export function setToken(token) {
  safeStorage.set(TOKEN_KEY, token);
  setAuthHeader(token);
}

export function setRefreshToken(token) {
  safeStorage.set(REFRESH_KEY, token);
}

export function storeSession(data) {
  if (!data) return;
  if (data.access_token) setToken(data.access_token);
  if (data.refresh_token) setRefreshToken(data.refresh_token);
}

export function clearSession() {
  safeStorage.set(TOKEN_KEY, null);
  safeStorage.set(REFRESH_KEY, null);
  setAuthHeader(null);
}

/** Decode the JWT payload without verifying the signature (client side only). */
function decodePayload(token) {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const binary = window.atob(padded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

/** Access token expiry in ms since epoch, or null when unknown. */
export function tokenExpMs(token = getToken()) {
  const payload = token ? decodePayload(token) : null;
  return payload && payload.exp ? payload.exp * 1000 : null;
}

export function isAccessTokenExpired(token = getToken(), marginMs = 0) {
  if (!token) return true;
  const exp = tokenExpMs(token);
  if (!exp) return false; // unparseable: let the server decide
  return Date.now() + marginMs >= exp;
}

/**
 * Exchange the refresh token for a new pair. Single-flight: concurrent callers
 * share the same request so we never rotate the token twice in parallel.
 * Returns the new user payload, or null when the session is really gone.
 */
export function refreshSession() {
  if (refreshPromise) return refreshPromise;

  const refreshToken = getRefreshToken();
  refreshPromise = axios
    .post(
      `${API}/auth/refresh`,
      refreshToken ? { refresh_token: refreshToken } : {},
      { _skipAuthRefresh: true, withCredentials: true }
    )
    .then(({ data }) => {
      storeSession(data);
      return data;
    })
    .catch((error) => {
      const status = error?.response?.status;
      // 401/403 => session is dead for good. Anything else (offline, 5xx,
      // CORS hiccup) is transient and must NOT log the user out.
      if (status === 401 || status === 403) clearSession();
      return null;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

/**
 * Make sure we hold a usable access token before an authenticated request.
 * Returns true when a token is available.
 */
export async function ensureFreshSession() {
  if (!getToken() && !getRefreshToken()) return false;
  if (!isAccessTokenExpired(getToken(), REFRESH_MARGIN_MS)) return true;
  const refreshed = await refreshSession();
  return Boolean(refreshed || getToken());
}

export const isAuthPath = (url = '') => AUTH_PATHS.some((path) => String(url).includes(path));

let interceptorsInstalled = false;

/**
 * Global axios behaviour:
 * - request: refresh proactively when the access token is close to expiry;
 * - response: on a 401, refresh once and replay the original request.
 */
export function installAuthInterceptors() {
  if (interceptorsInstalled) return;
  interceptorsInstalled = true;

  axios.defaults.withCredentials = true;

  axios.interceptors.request.use(async (config) => {
    if (config._skipAuthRefresh) return config;
    if (isAuthPath(config.url)) return config;
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    if ((token || getRefreshToken()) && isAccessTokenExpired(token, REFRESH_MARGIN_MS)) {
      const refreshed = await refreshSession();
      if (refreshed?.access_token) {
        config.headers.Authorization = `Bearer ${refreshed.access_token}`;
      } else if (!getToken()) {
        delete config.headers.Authorization;
      }
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config;
      const status = error?.response?.status;
      if (
        status !== 401 ||
        !config ||
        config.__isRetry ||
        config._skipAuthRefresh ||
        isAuthPath(config.url)
      ) {
        return Promise.reject(error);
      }

      config.__isRetry = true;
      const refreshed = await refreshSession();
      if (!refreshed?.access_token) return Promise.reject(error);
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${refreshed.access_token}`;
      return axios(config);
    }
  );
}

installAuthInterceptors();

export default {
  TOKEN_KEY,
  REFRESH_KEY,
  getToken,
  getRefreshToken,
  setToken,
  setRefreshToken,
  storeSession,
  clearSession,
  tokenExpMs,
  isAccessTokenExpired,
  refreshSession,
  ensureFreshSession,
  installAuthInterceptors,
};
